import type { TFunction } from "i18next";

// `value` é o que o backend espera (nome em português, estável); `i18nKey`
// expõe o `label` traduzido pelos factories `getBrazilianStates(t)` /
// `getCountryOptions(t)` consumidos pelos forms.
const BRAZILIAN_STATE_DEFINITIONS = [
  { i18nKey: "common.address.states.acre", value: "Acre" },
  { i18nKey: "common.address.states.alagoas", value: "Alagoas" },
  { i18nKey: "common.address.states.amapa", value: "Amapá" },
  { i18nKey: "common.address.states.amazonas", value: "Amazonas" },
  { i18nKey: "common.address.states.bahia", value: "Bahia" },
  { i18nKey: "common.address.states.ceara", value: "Ceará" },
  { i18nKey: "common.address.states.distritoFederal", value: "Distrito Federal" },
  { i18nKey: "common.address.states.espiritoSanto", value: "Espírito Santo" },
  { i18nKey: "common.address.states.goias", value: "Goiás" },
  { i18nKey: "common.address.states.maranhao", value: "Maranhão" },
  { i18nKey: "common.address.states.matoGrosso", value: "Mato Grosso" },
  { i18nKey: "common.address.states.matoGrossoDoSul", value: "Mato Grosso do Sul" },
  { i18nKey: "common.address.states.minasGerais", value: "Minas Gerais" },
  { i18nKey: "common.address.states.para", value: "Pará" },
  { i18nKey: "common.address.states.paraiba", value: "Paraíba" },
  { i18nKey: "common.address.states.parana", value: "Paraná" },
  { i18nKey: "common.address.states.pernambuco", value: "Pernambuco" },
  { i18nKey: "common.address.states.piaui", value: "Piauí" },
  { i18nKey: "common.address.states.rioDeJaneiro", value: "Rio de Janeiro" },
  { i18nKey: "common.address.states.rioGrandeDoNorte", value: "Rio Grande do Norte" },
  { i18nKey: "common.address.states.rioGrandeDoSul", value: "Rio Grande do Sul" },
  { i18nKey: "common.address.states.rondonia", value: "Rondônia" },
  { i18nKey: "common.address.states.roraima", value: "Roraima" },
  { i18nKey: "common.address.states.santaCatarina", value: "Santa Catarina" },
  { i18nKey: "common.address.states.saoPaulo", value: "São Paulo" },
  { i18nKey: "common.address.states.sergipe", value: "Sergipe" },
  { i18nKey: "common.address.states.tocantins", value: "Tocantins" },
] as const;

const COUNTRY_DEFINITIONS = [
  { i18nKey: "common.address.countries.brazil", value: "Brasil" },
] as const;

export const getBrazilianStates = (
  t: TFunction,
): { label: string; value: string }[] =>
  BRAZILIAN_STATE_DEFINITIONS.map(({ i18nKey, value }) => ({
    label: t(i18nKey),
    value,
  }));

export const getCountryOptions = (
  t: TFunction,
): { label: string; value: string }[] =>
  COUNTRY_DEFINITIONS.map(({ i18nKey, value }) => ({
    label: t(i18nKey),
    value,
  }));

// Normaliza o valor do estado vindo do back (que pode estar gravado tanto como
// abreviação — ex.: "RJ" — quanto como nome completo — ex.: "Rio de Janeiro") para
// o nome completo, que é o `value` esperado pelos `SelectSearch` que consomem
// `getBrazilianStates(t)`. Sem isso, registros antigos com abreviação ficam com o
// campo "Estado" vazio na edição (sem `value` correspondente nas opções).
export const normalizeStateToFullName = (state: string | undefined | null): string => {
  if (!state) return "";
  const upper = state.toUpperCase();
  return STATE_ABBREVIATION_MAP[upper] ?? state;
};

export const STATE_ABBREVIATION_MAP: Record<string, string> = {
  AC: "Acre",
  AL: "Alagoas",
  AP: "Amapá",
  AM: "Amazonas",
  BA: "Bahia",
  CE: "Ceará",
  DF: "Distrito Federal",
  ES: "Espírito Santo",
  GO: "Goiás",
  MA: "Maranhão",
  MT: "Mato Grosso",
  MS: "Mato Grosso do Sul",
  MG: "Minas Gerais",
  PA: "Pará",
  PB: "Paraíba",
  PR: "Paraná",
  PE: "Pernambuco",
  PI: "Piauí",
  RJ: "Rio de Janeiro",
  RN: "Rio Grande do Norte",
  RS: "Rio Grande do Sul",
  RO: "Rondônia",
  RR: "Roraima",
  SC: "Santa Catarina",
  SP: "São Paulo",
  SE: "Sergipe",
  TO: "Tocantins",
};
